export type DateString = string;

export interface IPopularKeywordDto {
  id?: number;
  keyword: string;
  order: number;
  cnt?: number;
}

export interface IPopularKeywordsResponseDto {
  keyword: IKeyword[];
}

export type ISuggestionSourceType = 'story' | 'season' | 'user' | 'writer' | 'penname' | 'theme';

interface ISuggestionSourceBase {
  id: string;
  type: ISuggestionSourceType;
  suggest: unknown;
}

export interface ISuggestionSourcePenname extends ISuggestionSourceBase {
  type: 'penname';
  penname: string;
}

export interface ISuggestionSourceStory extends ISuggestionSourceBase {
  type: 'story';
}

export interface ISuggestionSourceUser extends ISuggestionSourceBase {
  type: 'user';
  is_writer: boolean;
  writer_name: string[];
}

export type ISuggestionSource = ISuggestionSourceStory | ISuggestionSourceUser | ISuggestionSourcePenname;

export interface ISuggestion {
  text: string;
  highlight: string;
  _source: ISuggestionSource;
}

export interface ILateNightKeyword {
  id: number;
  keyword: string;
}

export interface ISignInRequestDto {
  username: string;
  password: string;
}

export interface ICollection {
  id?: number;
  trackingId?: string;
  availableCountryCountText: string;
  displayType: ICollectionDisplayType;
  label: string;
  title: string;
  internalTitle: string;
  description: string;
  coverUrl?: string;
  layoutColumns: number;
  backgroundColor: string;
  type: CollectionType;
  storyIds?: number[];
  targetIds?: IStoryOrSeasonId[];
  stories: IStory[];
  createdAt?: string;
  storyGenreTextMaps?: IStoryGenreTextMap[];
  autoGenerated?: boolean;
  showRanking: boolean;
  randomOrder: boolean;
  priority: number | undefined;
  storyCount: number;
  storyTypes?: StoryType[];
  iconUrl?: string;
  subTitle?: string;
  expiredAt?: Date;
  discountRate?: number;
  subBackgroundColor?: string;
  isOnboarding: boolean;
  isImageHidden: boolean;

  homesets: IHomeset[];
  linkCount: number;
  homesetSplits: IHomesetSplit[];
  displayStyleId?: number;
  mappers: ICollectionStoryMapper[];
  linkedCurations?: ISegmentCuration[];

  // Only to display data.
  active?: boolean;
  isAlerted?: boolean;
  errorMessage?: string;
  isRemoving?: boolean;
  notify?: boolean;
  targets?: IStoryOrSeasonId[];
}
export enum ICollectionDisplayType {
  Event = 'event',
  General = 'general',
  Sale = 'sale',
}
export interface ICollectionStoryMapper {
  collectionId: number;
  storyId: number;
  seasonId?: number;
}
export interface IStoryOrSeasonId {
  storyId: number;
  seasonId?: number | null;
}

export interface IStoryGenreTextMap {
  storyId: number;
  storyGenreText: string | null;
}

interface ICollectionCreatorExclusive {
  sql: string;
  intervalType: IntervalType;
  intervalValue: number;
  immediately?: boolean;
  period: number;
  nextAt?: string;
  isActive?: boolean;
  intervalValueAndPeriod?: string;
}

export interface IReportInfo {
  id: number;
  category1: string;
  category2: string | null;
  category3: string | null;
  createdAt: string;
  status: 'open' | 'resolved' | 'dismissed';
  storyId: string;
  episodeId: number | null;
  targetObject: string;
  text: string;
  updatedAt: string;
  userId: number;
}

export interface IReportsResponse {
  [0]: IReportInfo[];
  [1]: number;
}

export type ICollectionCreator = ICollection & ICollectionCreatorExclusive;

interface AbstractUserSegment {
  id?: number;
  type: UserSegmentType;
  name: string;
  description: string;
  createdAt?: string;
  daysToExpiration?: string; // dte == days to expiring == max attendance duration
  userCount?: number;
  brazeTriggerType?: UserSegmentBrazeTriggerType;
  brazeTriggerId?: string;
  brazeTriggerWhenReentering?: boolean;
  isActive?: boolean;
  isGreedy?: boolean;
  persistentType?: UserSegmentPersistentType;
  collectionNum?: number;
  featuredCardNum?: number;
  isFeaturedCardsOnTest?: boolean;
  isCollectionsOnTest?: boolean;

  // for request
  dte?: string;
}

export interface IUserSegmentGroup extends AbstractUserSegment {
  type: UserSegmentType.Group;
  children?: IUserSegment[];
  childSegmentIds?: number[];
}
export interface IUserSegmentAutoUpdate extends AbstractUserSegment {
  type: UserSegmentType.AutoUpdate;
  autoupdateOption: IUserSegmentAutoUpdateOption;
}

export interface IUserSegmentTrigger extends AbstractUserSegment {
  type: UserSegmentType.Trigger;
  triggerOption: IUserSegmentTriggerOption;
}

export interface IUserSegmentWebhook extends AbstractUserSegment {
  type: UserSegmentType.Webhook;
  webhookOption: IUserSegmentWebhookOption;
}

export interface IUserSegmentProductProduct {
  createdAt: DateString;
  id: number;
  productId: string;
  segmentId: number;
}

export interface IUserSegmentPromotions {
  instantBanner: string;
  instantBannerColor: string;
  leftProduct: string;
  leftProductForSubscription: string;
  rightProduct: string;
  rightProductForSubscription: string;
  storeBanner: string;
  storeBannerColor: string;
  starterProduct: string;
}

export interface IUserSegmentProduct extends AbstractUserSegment {
  type: UserSegmentType.Product;
  products: Array<IUserSegmentProductProduct>;
  promotions: IUserSegmentPromotions;
}

export interface IUserSegmentSurvey extends AbstractUserSegment {
  type: UserSegmentType.Survey;
}
export interface IUserSegmentSystem extends AbstractUserSegment {
  type: UserSegmentType.System;
}
export interface IUserSegment extends AbstractUserSegment {
  autoupdateOption?: IUserSegmentAutoUpdateOption;
  triggerOption?: IUserSegmentTriggerOption;
  webhookOption?: IUserSegmentWebhookOption;
  children?: IUserSegment[];
  childSegmentIds?: number[];
  dte?: string;
  products?: Array<IUserSegmentProductProduct>;
  promotions?: IUserSegmentPromotions;
}

export type IUserSegments =
  | IUserSegmentProduct
  | IUserSegmentSurvey
  | IUserSegmentGroup
  | IUserSegmentAutoUpdate
  | IUserSegmentTrigger
  | IUserSegmentWebhook
  | IUserSegmentSystem;

export interface IUserSegmentGroupOption extends AbstractUserSegment {
  id?: number;
  segmentId?: number;
  persistentType: UserSegmentPersistentType;
  isReenterable: boolean;
  isGreedy: boolean;
  intervalType: IntervalType;
  intervalValue: number;
  period: number;
  isActive?: boolean;
  sql: string;
  nextAt?: string;
  immediately?: boolean;
}

export interface IUserSegmentTriggerOption {
  id?: number;
  segmentId?: number;
  trigger: TriggerType;
  properties: string[];
  isActive?: boolean;
  persistentType: UserSegmentPersistentType;
  isReenterable: boolean;
  isGreedy: boolean;
}

export interface IUserSegmentAutoUpdateOption {
  id?: number;
  segmentId?: number;
  persistentType: UserSegmentPersistentType;
  isReenterable: boolean;
  isGreedy: boolean;
  intervalType: IntervalType;
  intervalValue: number;
  period: number;
  isActive?: boolean;
  sql: string;
  nextAt?: string;
  immediately?: boolean;
}

export interface IUserSegmentWebhookOption {
  id?: number;
  segmentId?: number;
  persistentType: UserSegmentPersistentType;
  isReenterable: boolean;
  isGreedy: boolean;
  isActive?: boolean;
}

export interface IStory {
  availableCountryCountText: string;
  availableCountries: string[];
  createdAt: string;
  deletedAt: string | null;
  displayCoins: boolean;
  earnCoins: number;
  episodeCount: number;
  genre: IGenre;
  genreId: number;
  heartbeatPublish: boolean;
  id: number;
  isCompleted: boolean;
  isCustomSeasonTitle: boolean;
  isDeleted: boolean;
  isScheduled: boolean;
  lastPublishedAt: string | null;
  loveCount: number;
  popularity: number;
  programType: string;
  publisherIcon: string | null;
  salesType: string;
  seasons: ISeason[];
  seasonCount: number;
  startedAt: string | null;
  status: string;
  storyGenreText?: string;
  themeText: string;
  themes: { main?: ITheme[]; detailed?: ITheme[] };
  title: string;
  type: StoryType;
  unlockAt: string | null;
  updatedAt: string | null;
  viewCount: number;
  writer: IWriter;
  writerId: number;
  seasonId?: number;
  episodePrice: number;
  penname: IPenname;
  pennameId: number;
  pennames: IPenname[];
  firstSeason: ISeason;
}
export interface ISeason {
  additionalCoverHUrl: string;
  additionalCoverVUrl: string;
  coverUrl: string | null;
  createdAt: string;
  deletedAt: string | null;
  earnCoins: number;
  episodeCount: number;
  id: number;
  ipType: string;
  lastPublishedAt: string | null;
  loveCount: number;
  no: number;
  seasonNo: number;
  startedAt: string | null;
  summary: string;
  tagLine: string | null;
  title: string;
  updatedAt: string | null;
  viewCount: number;
  story: IStory;
  storyId: number;
  paymentWriter: IWriter;
}

export interface ILink {
  id?: number;
  title: string;
  type: 'internal' | 'external' | 'view';
  label?: string;
  desc?: string;
  url: string;
  coverUrl?: string;
  createdAt?: string;
  updatedAt?: string;
  isActive?: boolean;
}

export interface ILabeledThemes {
  label: string;
  themes: ITheme[];
}

export interface ITheme {
  category: string;
  id: number;
  isPopular: boolean;
  order: number;
  storyCount: number;
  subTitle: string | null;
  targetUrl: string | null;
  title: string;
  type: string;
}

export interface IUser {
  accessToken: string;
  bio: string;
  depositCoins: number;
  email: string;
  facebook: string | null;
  hasAgreed: boolean;
  hasPassword: boolean;
  homepage: string;
  id: number;
  instagram: string;
  isFbUser: boolean;
  isPublisher: boolean;
  isVerified: boolean;
  isWriter: boolean;
  joinedAt: string;
  level: number;
  nickname: string;
  stringId: string | null;
  stringIdLower: string | null;
  subscriberCount: number;
  thumbUrl: string | null;
  twitter: string | null;
  username: string;
  usernameLower: string;
  displayName: string;
  displayNameStatus: ProfileImageReviewStatus; // TODO
  photoUrl: string;
  photoStatus: ProfileImageReviewStatus;
  isBanned: boolean;
  bannedAt: string;
}

export interface IWriterPermission {
  id: number;
  userId: number;
  canUseAllPennames: boolean;
  canCreatePennames: boolean;
  canGrantPaymentAccount: boolean;
  canGrantStoryEditor: boolean;
  createdAt: string;
}
export interface IWriter extends IUser {
  permission: IWriterPermission;
  ownedPennames: IPenname[];
  licensedPennames: IPenname[];
  joinedAt: string;
  representPenname?: IPenname;
}

export interface IPenname {
  id: number;
  name: string;
  slug: string;
  email: string;
  thumbUrl: string;
  bio: string;
  homepage: string;
  facebook: string;
  twitter: string;
  instagram: string;
  createdAt: Date;
  editorId: number;
  editor: Partial<IUser>;
  licensees: Partial<IUser>[];
  assigneeIds: number[];
  storyCount: number;
}

export interface ISeries {
  createdAt: string | null;
  id: number;
  stringId: string | null;
  title: string;
  updatedAt: string | null;
  views: number;
  writerId: number;
  writerStringId: string | null;
}

export interface IRedeemCode {
  id: number;
  code: string;
  title: string;
  type: 'story-coupon';
  storyId: number;
  expiredAt: Date;
  count: number;
  amount: number;
  validityPeriod: number;
  isValid: boolean;
  story: IStory;
  redeemUrl: string;
}

export interface IRedeemCodes {
  hasMore: boolean;
  redeemCodes: IRedeemCode[];
}

export interface IRedeemCodeCreateRequestDto {
  type: 'story-coupon' | 'coin';
  title?: string;
  storyId?: number;
  expiredAt?: Date;
  validityPeriod?: number;
  amount?: number;
  count?: number;
}

export interface IGiftboxCreateRequestDto {
  title: string;
  giftboxStories: {
    storyId: number;
    numberOfCoupons: number;
    validityPeriod: number;
    order?: number;
  }[];
}

export interface IGiftboxUpdateRequestDto {
  title: string;
  giftboxStories: {
    id?: number;
    storyId: number;
    numberOfCoupons: number;
    validityPeriod: number;
    order: number;
  }[];
}

export interface IGiftbox {
  id: number;
  title: string;
  startDate: Date | null;
  createdAt: Date;
  isLive: boolean;
}

export interface IGiftboxStory {
  id?: number;
  storyId: string;
  story?: IStory;
  numberOfCoupons: number | string;
  validityPeriod: number | string;
  deletedAt?: Date;

  // 아래껀 클라전용
  isValid: boolean;
  error?: string;
  errorNumberOfCoupons?: boolean;
  errorValidityPeriod?: boolean;
}

interface IPaginationTemplate<T> {
  [key: string]: T[];
}
interface ISearchCollectionResultTemplate<T> {
  items: {
    type: string;
    collection: T;
  }[];
}
interface ISearchLaneResultTemplate<T> {
  items: {
    type: string;
    lane: T;
  }[];
}

interface IPagination {
  hasMore: boolean;
  totalCount?: number;
}

export type IPaginationResponseDto<T> = IPaginationTemplate<T> & IPagination;
export type ISearchCollectionResponseDto<T> = ISearchCollectionResultTemplate<T> & IPagination;
export type ISearchLaneResponseDto<T> = ISearchLaneResultTemplate<T> & IPagination;

export interface IFeaturedCardSet {
  id: number;
  trackingId?: string;
  featuredTitle: string;
  internalTitle: string;
  cardCount: number;
  linkCount: number;
  createdAt: string;
  featuredCards?: IFeaturedCard[];
  homesets: IHomeset[];
  homesetSplits: IHomesetSplit[];

  priority: number | undefined;

  // Display Data
  isAlerted?: boolean;
  errorMessage?: string;
  isRemoving?: boolean;
}

export interface IKeyword {
  keyword: string;
  rank: number;
  isSelected: boolean;
}

export interface IFeaturedCard {
  id?: number;
  type: FeaturedCardType;
  title?: string;
  label?: string;
  isEvent?: boolean;
  coverUrl?: string | null;
  subTitle?: string;
  actionText?: string;

  createdAt?: Date;

  object?: IFeaturedCardObject;

  storyTypes?: StoryType[];
  targetId?: string;
  desc?: string;
  linkedCurations?: ISegmentCuration[];
  displayCoverUrl?: string;
  availableCountryCountText?: string;

  // 호환 때문에 남겨둠
  no?: number;
  objectId?: number | string;
}

export enum FeaturedCardType {
  Story = 'story',
  Season = 'season',
  Link = 'link',
  Collection = 'collection',
}

export interface IFeaturedCardObject {
  id?: number;
  label?: string;
  title?: string;
  desc?: string;
  coverUrl?: string;
  story: IStory;
  storyTypes?: StoryType[];
}

export interface ISearchResultDto {
  items: ISearchResult[];
  hasMore: boolean;
  emptyResult?: { title: string; genres: IGenre[] };
}

export interface IGenre {
  id: number;
  title: string;
}

export interface ISearchResult {
  id?: number;
  type: 'story' | 'season' | 'writer' | 'theme';
  story?: IStory;
  writer?: IWriter;
  theme?: ITheme;
}

export interface ILinkResponse {
  links: ILink[];
  hasMore?: boolean;
  totalCount: number;
}

export enum ISegmentCurationType {
  FeaturedCards = 'featured_cards',
  FictionSerials = 'fiction_serials',
  AudioFeatured = 'audio_featured',
  AudioCollections = 'audio_collections',
  TextFeatured = 'text_featured',
  TextCollections = 'text_collections',
  Onboarding = 'onboarding',
}

export type ISegmentCurationFeaturedCardType =
  | ISegmentCurationType.AudioFeatured
  | ISegmentCurationType.FeaturedCards
  | ISegmentCurationType.TextFeatured;

export type ISegmentCurationCollectionType =
  | ISegmentCurationType.AudioCollections
  | ISegmentCurationType.FictionSerials
  | ISegmentCurationType.TextCollections;

export enum ISegmentCurationStatus {
  Activated = 'activated',
  Scheduled = 'scheduled',
  Completed = 'completed',
}

export interface ISegmentCuration {
  id?: number;
  segmentId?: number;
  type: ISegmentCurationType;
  targetId: string;
  priority: number;
  startsAt: Date | null;
  endsAt: Date | null;
  linkedCurations?: ISegmentCuration[];
  abTests?: ISegmentCurationAbTest[];
  object?: IFeaturedCard | ICollection;

  // Display Data
  segment?: IUserSegment;
  isActive?: boolean;
  isAlerted?: boolean;
  errorMessage?: string;
  isRemoving?: boolean;
}

export interface ISegmentCurationFeaturedCard extends ISegmentCuration {
  type: ISegmentCurationFeaturedCardType;
  object: IFeaturedCard;
}
export interface ISegmentCurationCollection extends ISegmentCuration {
  type: ISegmentCurationCollectionType;
  object: ICollection;
}

export enum AbTestType {
  HomeCuration = 'HOME_CURATION',
}

export interface IAbTestUpdateRequestDto {
  startsAt: Date | string;
  endsAt: Date | string;
}
export interface ISegmentCurationAbTest {
  id?: number;
  type: AbTestType;
  testTargetValue?: number;
  startsAt?: Date | null;
  endsAt?: Date | null;
  variants?: IAbTestVariant[];

  // display
  homeCuration?: ISegmentCurationFeaturedCard | ISegmentCurationCollection;
}

export interface ISegmentCurationAbTestRequest {
  id?: number;
  abTest?: IAbTestUpdateRequestDto;
  variants?: IAbTestVariant[];
}

export interface IAbTestVariant {
  id?: number;
  abTestId?: number;
  data?: IAbTestVariantData;
  // display
  object?: IFeaturedCard | ICollection;
}

export interface IAbTestVariantData {
  type: 'featured_card' | 'collection';
  id?: number; //featuredcard
  trackingId?: string; // collections
}

export interface ISegmentAbtestUpdateRequestDto {
  startsAt: Date | string;
  endsAt: Date | string;
}
export interface ISegmentCurationUpdateRequestDto {
  priority: number;
  startsAt?: Date | string | null;
  endsAt?: Date | string | null;
}

export interface IHomeCuration {
  featuredTitle: string;
  homeCurations: ISegmentCuration[];
}

export interface IHomeset {
  id: number;
  segmentId: number;
  isActive: boolean;
  isScheduled: boolean;
  createdAt?: Date;
  deletedAt?: Date;
  startedAt?: Date;
  endAt?: Date;
  systemType?: HomesetSystemType;
  internalTitle?: string;
  homesetSplits: IHomesetSplit[];
  segment: IUserSegment;
  notify?: boolean;
}

export interface IHomesetSplit {
  id?: number;
  homesetId?: number;
  featuredCardSetId?: number;
  deletedAt?: Date;
  homeset?: IHomeset;
  featuredCardSet?: IFeaturedCardSet;
  collections?: ICollection[];
}

export interface IHomesetSplitElementDisplayData {
  isAlerted: boolean;
  errorMessage: string;
  isRemoving: boolean;
}

export interface IHomesetRequestDto {
  segmentId?: number;
  homesetSplits: IHomesetSplit[];
  collectionPriorities: CollectionPrioritiesRequestDto[];
  featuredCardSetPriorities: FeaturedCardSetPrioritiesRequestDto[];
}

interface CollectionPrioritiesRequestDto {
  trackingId: string;
  priority: number;
}

interface FeaturedCardSetPrioritiesRequestDto {
  id: number;
  trackingId: string;
  priority: number;
}

export interface IDisplayStyle {
  id: number;
  type: 'card' | 'list';
  shape: 'horizontal-rectangle' | 'vertical-rectangle';
  numberOfDisplay: number;
  title: string;
  requiredVersion: null;
  createdAt: Date;
  deletedAt: Date;
  collectionNumber: number;
}

export interface ICollectionRequestDtoWithVerifies {
  originalCollections: Partial<ICollection>[];
  newCollections: Partial<ICollection>[];
}

export interface IFeaturedCardSetRequestDtoWithVerifies {
  originalFeaturedCardSets: Partial<IFeaturedCardSet>[];
  newFeaturedCardSets: Partial<IFeaturedCardSet>[];
}

export interface IHomeDisplayStyleCreateRequestDto {
  title: string;
  type: HomeStyleType;
  shape: HomeShape;
  numberOfDisplay: number;
  requiredVersion: string;
}

export interface IHomeDisplayStyle {
  id: number;
  title: string;
  type: HomeStyleType;
  shape: HomeShape;
  numberOfDisplay: number;
  requiredVersion: string;
  createdAt: string;
  deletedAt?: string;
  collectionNumber: number;
}

export interface IFlowKey {
  key: string;
}

export enum CollectionType {
  FictionSerials = 'fictionSerials',
  ChatStories = 'chatStories',
  Writers = 'writers',
}

export enum IntervalType {
  Monthly = 'month',
  Weekly = 'week',
  Daily = 'day',
  Hourly = 'hour',
  Minutely = 'minute', // WARNING: JUST QA CONVENIENCE
}

export enum UserSegmentType {
  Product = 'product',
  Survey = 'survey',
  AutoUpdate = 'autoupdate',
  Trigger = 'trigger',
  Webhook = 'webhook',
  Group = 'group',
  System = 'system',
}

export enum TriggerType {
  JoinedRadish = 'joined_radish',
  OnboardingFlow = 'onboarding_flow',
  SegmentAttendanceExpired = 'segment_attendance_expired',
  ReferralCode = 'referral_code',
  RedeemCode = 'redeem_code',
}

export enum UserSegmentPersistentType {
  Long = 'long',
  Short = 'short',
}

export enum HomesetSystemType {
  NewUser = 'newUser',
  OldUser = 'oldUser',
  AllUser = 'allUser',
}

export enum HomeStyleType {
  Card = 'card',
  List = 'list',
  Banner = 'banner',
}

export enum HomeShape {
  HorizontalRectangle = 'horizontal-rectangle',
  VerticalRectangle = 'vertical-rectangle',
  Square = 'square',
  Circle = 'circle',
}

export enum LinkType {
  Internal = 'internal',
  External = 'external',
  View = 'view',
}

export interface ILuckyDraw {
  id: number;
  title: string;
  description: string;
  segmentId: number;
  eventType: LuckyDrawEventType;
  chatroomStoryId: number;
  participantsNum: number;
  countdownMinutes: number;
  cooldownMinutes: number;
  expiredAt?: Date;
  createdAt: Date;
  startsAt?: Date;
  deletedAt?: Date;
  gifts: ILuckyDrawGift[];
}

export interface ILuckyDrawGift {
  type: LuckyDrawGiftType;
  amount?: number;
  winnerNum: number;
  targetId?: number;
  redirectTo: string;
  validForDays?: number;
}

export interface ILuckyDrawCreatorExclusive {}

export type ILuckyDrawCreator = ILuckyDraw & ILuckyDrawCreatorExclusive;

export enum LuckyDrawGiftType {
  Coin = 'coin',
  Coupon = 'coupon',
  Sale = 'sale',
  Lose = 'lose',
}

export enum LuckyDrawEventType {
  General = 'general',
  Chatroom = 'chatroom',
  Giftbox = 'giftbox',
}

export interface IReadingChallenge {
  id: number;
  segmentId: number;
  storyId: number;
  rewardNum: number;
  startAt: Date;
  endAt: Date;
  internalTitle: string;
  internalDescription: string;
  storySummary: string;
  deletedAt: Date;
  story: IStory;
}

export interface IReadingChallengeCreatorExclusive {}

export type IReadingChallengeCreator = IReadingChallenge & IReadingChallengeCreatorExclusive;

export interface ISuccessResult<T> {
  success: true;
  data: T;
}

export interface IFailResult {
  success: false;
  errorCode: string;
  message: string;
}

export type IApiResult<T> = ISuccessResult<T> | IFailResult;

export enum ModerationType {
  Comments = 'comments',
  ProfileImages = 'profile_images',
  DisplayNames = 'display_names',
}

export enum CommentReviewStatus {
  All = 'all',
  NotReviewed = 'not_reviewed',
  Approved = 'approved',
  Pending = 'pending',
  Hidden = 'hidden',
  /**
   * HIDDEN_BY_BAN, BANNED: 댓글의 Review Status 에는 실제로 DB에 Ban 이라는 상태를 저장하지는 않습니다.
   * 유저의 다른 어떤 댓글이 규제 되면 이외 모든 댓글들의 isBannedUser 속성이 true 가 되고, 이 시점부터는 댓글의 리뷰상태와 무관하게
   * 클라이언트와 어드민의 댓글 리스트에서는 HIDDEN_BY_BAN 상태로 처리합니다.
   * BANNED 상태는 실제로 *규제* 된 댓글을 의미하며, Banned User 탭에 가면 이 유저가 어떤 댓글때문에 규제 된 것인지 확인할 수 있습니다.
   * 이 시점에 직접 규제된 이 댓글은 hidden 으로 상태가 변경됩니다.
   */
  HiddenByBan = 'hidden_by_ban',
  Banned = 'banned',
  /**
   * UNBAN: 마찬가지로 댓글이 실제로 DB에 이런 상태를 갖는 것은 아니고, BANNED 된 유저를 UNBAN 하고자 요청 파라메터에 사용하기 위한 Enum 입니다.
   * 이렇게 unban 된 유저의 댓글 중 규제와 무관한 것들은 기존의 (실제로는 값이 바뀐적이 없기에) 상태로 노출 됩니다.
   * 직접 규제된 이 댓글은 규제 시점에 hidden 로 변경되었기에, 이 값을 unban 이후 유지합니다.
   */
  Unban = 'unban',
}

export enum ProfileImageReviewStatus {
  NotReviewed = 'not_reviewed',
  Approved = 'approved',
  Pending = 'pending',
  Blocked = 'blocked',
}

export enum DisplayNameReviewStatus {
  NotReviewed = 'not_reviewed',
  Approved = 'approved',
  Pending = 'pending',
  Blocked = 'blocked',
}

export enum ModerationSearchType {
  UserId = 'user_id',
  StoryId = 'story_id',
  SeasonId = 'season_id',
  CommentId = 'comment_id',
  DisplayName = 'display_name',
  UserName = 'user_name',
}

export enum ReviewSortOrder {
  Newest = 'newest',
  Oldest = 'oldest',
  Reported = 'reported',
}

export type CommentReviewId = number;
export type ProfileImageReviewId = number;
export type DisplayNameReviewId = number;
export type UserId = number;
export type CommentId = number;
export type ProfileImageId = number;
export type DisplayNameId = number;
export type StoryId = number;
export type EpisodeId = number;

interface IReview<T> {
  id: T;
  adminUserId: UserId;
  note: string;
  createdAt: string;
  updatedAt: string;
  bannedAt: string;
  adminUsername: string;
}

export interface ICommentReview extends IReview<CommentReviewId> {
  commentId: CommentId;
  status: CommentReviewStatus;
}

export interface IProfileImageReview extends IReview<ProfileImageReviewId> {
  userPhotoId: ProfileImageId;
  status: ProfileImageReviewStatus;
}

export interface IDisplayNameReview extends IReview<DisplayNameReviewId> {
  userDisplayNameId: DisplayNameId;
  status: DisplayNameReviewStatus;
}

export interface IViolationHistory {
  id: number;
  userId: number;
  reporterId: number;
  status: string;
  reason: string;
  reportedType: string;
  reportedId: number;
  createdAt: DateString;
  reportedTarget?: {
    id: number;
    commentId: number;
    adminUserId: number;
    status: string;
    bannedAt: DateString;
    note: string;
    createdAt: DateString;
    updatedAt: DateString;
    comment: IComment;
  };
}
export interface IBannedUser {
  id: number;
  photoUrl: string;
  displayName: string;
  nickname: string;
  username: string;
  bannedAt: DateString;
  latestViolationHistory: IViolationHistory;
  violationHistories?: IViolationHistory[];
}

export interface IComment {
  episodeNo: number;
  seasonId: number;
  id: CommentId;
  parentId: CommentId;
  hasReply: boolean;
  isWriterReplied: boolean;
  episodeId: EpisodeId;
  storyId: StoryId;
  userId: UserId;
  isBannedUser: boolean;
  isWrittenByWriter: boolean;
  isReportedByWriter: boolean;
  username: string;
  displayName: string;
  displayNameId: DisplayNameId;
  photoUrl: string;
  photoId: ProfileImageId;
  type: string;
  contents: string;
  originalContents: string;
  loveCount: number;
  isLovedByWriter: boolean;
  createdAt: string;
  deletedAt: string;
  lastReportedAt: string;
  isHidden: boolean;
  isLoved: boolean;
  review: ICommentReview;
  reportedCount: number;
  reportReason: string;
}

export interface IBannedUserRequestParamDto {
  queryType?: string;
  q?: string;
  sortBy?: ReviewSortOrder;
}

export interface ICommentRequestParamDto {
  startDate?: string;
  endDate?: string;
  reviewStatus?: CommentReviewStatus;
  userId?: UserId;
  storyId?: StoryId;
  seasonId?: number;
  commentId?: number;
  displayName?: string;
  order?: ReviewSortOrder;
}

export interface IProfileImageRequestParamDto {
  startDate?: string;
  endDate?: string;
  reviewStatus?: ProfileImageReviewStatus;
  userId?: UserId;
  username?: string;
  displayName?: string;
  order?: ReviewSortOrder;
}

export interface IDisplayNameRequestParamDto {
  startDate?: string;
  endDate?: string;
  reviewStatus?: DisplayNameReviewStatus;
  userId?: UserId;
  username?: string;
  displayName?: string;
  order?: ReviewSortOrder;
}

export type ReportId = number;
export interface ICommentReport {
  id: ReportId;
  commentId: CommentId;
  reporterId: UserId;
  reason: string;
  createdAt: string;
  reporter: IUser | null;
}

export interface IProfileImage {
  id: ProfileImageId;
  userId: UserId;
  isLatest: boolean;
  reportedCount: number;
  createdAt: string;
  lastReportedAt: string;
  user: IUser | null;
  review: IProfileImageReview;
  photoUrl: string;
}

export interface IDisplayName {
  id: DisplayNameId;
  userId: UserId;
  displayName: string;
  isLatest: boolean;
  reportedCount: number;
  createdAt: string;
  lastReportedAt: string;
  user: IUser | null;
  review: IDisplayNameReview;
  changedCount: number;
}

export interface IBlockedWord {
  id: number;
  contents: string;
  createdAt: string;
}

export type IBlockedDisplayName = IBlockedWord;

export interface IFreeStorySchedule {
  id: number;
  storyId: number;
  startAt: Date;
  endAt: Date;
  story?: IStory;
}

export interface IFreeStoryScheduleCreatorExclusive {}

export type IFreeStoryScheduleCreator = IFreeStorySchedule & IFreeStoryScheduleCreatorExclusive;

export enum AB_TEST_TYPE {
  DISPLAY = 'DISPLAY',
  PERFORMANCE = 'PERFORMANCE',
  STORY = 'STORY',
  SEASON = 'SEASON',
  EPISODE = 'EPISODE',
  BUNDLE = 'BUNDLE',
}

export enum AB_TEST_TARGET_TYPE {
  // noinspection JSUnusedGlobalSymbols
  FEATUREDCARDS = 'FEATURED_CARDS',
  CAROUSEL = 'CAROUSEL',
  DAILY_PICKS = 'DAILY_PICKS',
  COLLECTION = 'COLLECTION',
  STORY = 'STORY',
  SEASON = 'SEASON',
  PRODUCT = 'PRODUCT',
  EPISODE = 'EPISODE',
}

export enum AB_TEST_AUDIENCE_TYPE {
  // noinspection JSUnusedGlobalSymbols
  RANDOM = 'RANDOM',
  CONDITIONAL = 'CONDITIONAL',
  SEGMENT_OLD = 'SEGMENT',
  SEGMENT = 'SEGMENT_V2',
  SEGMENT_NO_CONTROL = 'SEGMENT_NO_CONTROL',
}

export interface IABTestBase {
  variants: Array<IPerformanceVariant | IDisplayVariant | IEpisodeVariant | IBundleVariant>;
  audienceType?: AB_TEST_AUDIENCE_TYPE;
  audienceValue?: number;
  createdAt?: Date;
  endDate?: Date;
  id: number | null;
  isUsable?: boolean;
  segmentId?: number;
  segment?: IUserSegment;
  startDate?: Date;
  testName: string;
  testTargetType?: AB_TEST_TARGET_TYPE | '';
  testTargetValue?: string;
  // testType?: AB_TEST_TYPE | '';
}

interface IABTestPerformance extends IABTestBase {
  testType: AB_TEST_TYPE.PERFORMANCE;
}
interface IABTestPerformanceFeaturedCard extends IABTestPerformance {
  testTargetType: AB_TEST_TARGET_TYPE.FEATUREDCARDS;
  variants: Array<IPerformanceFeaturdCardVariant>;
}
interface IABTestPerformanceCollection extends IABTestPerformance {
  testTargetType: AB_TEST_TARGET_TYPE.COLLECTION;
  variants: Array<IPerformanceCollectionVariant>;
}

interface IABTestDisplay extends IABTestBase {
  testType: AB_TEST_TYPE.DISPLAY;
  variants: Array<IDisplayVariant>;
}

interface IABTestEpisode extends IABTestBase {
  testType: AB_TEST_TYPE.EPISODE;
  variants: Array<IEpisodeVariant>;
}

interface IABTestBundlePackage extends IABTestBase {
  testType: AB_TEST_TYPE.BUNDLE;
  variants: Array<IBundleVariant>;
}

export type IABTest = IABTestPerformanceFeaturedCard | IABTestPerformanceCollection | IABTestDisplay | IABTestEpisode | IABTestBundlePackage;

// export interface IStoryVariant {
//   id?: number;
//   data: {
//     type: 'story' | 'season';
//     storyId?: number; // season일 때는 storyid, seasonid 동시 필요
//     seasonId?: number;
//     objectId?: number;
//     subTitle?: string;
//     actionText: string;
//     storyGenreText: string;
//   };
// }

export interface IPerformanceFeaturdCardVariant {
  id?: number;
  data: {
    type: 'story' | 'season';
    objectId: number;
    subTitle: string;
    actionText: string;
  };
}

export interface IPerformanceCollectionVariant {
  id?: number;
  data: {
    type: 'story' | 'season';
    storyId: number; // season일 때는 storyid, seasonid 동시 필요
    seasonId?: number;
    storyGenreText?: string;
  };
}

export type IPerformanceVariant = IPerformanceFeaturdCardVariant | IPerformanceCollectionVariant;

export interface IDisplayVariant {
  id?: number;
  data: {
    id?: number;
    title: string;
    summary: string;
    tagLine: string;
    additionalCoverVUrl: string;
    additionalCoverHUrl: string;
  };
}

export interface IEpisodeVariant {
  id?: number;
  data: any;
}

interface IBundleVariantData {
  type: string;
  bundles: IBundlePackage[];
}

export interface IBundleVariant {
  id?: number;
  data: IBundleVariantData;
  object?: any;
  bundles?: Partial<IBundlePackage>[];
}
export interface IABTestBundlePackageDto {
  targetType: 'ab_test';
  abTest: IABTest;
}

export interface IABTestListOption {
  testType?: AB_TEST_TYPE | 'all' | '';
  activeOnly?: string;
}

export enum UserSegmentBrazeTriggerType {
  None = 'none',
  Campaign = 'campaign',
  Canvas = 'canvas',
}

export enum RelatedStoryType {
  Episode = 'episode',
  StoryCover = 'story_cover',
  Paywall = 'paywall',
}

export interface IRelatedStoryMapper {
  id?: number;
  storyId: number;
  type: RelatedStoryType;
  segmentId: number;
  relatedStoryId: number;
  priority: number;
  relatedStory?: IStory;
  segment?: IUserSegment;
}

export interface IRelatedStoriesData {
  [key: string]: {
    segment: IUserSegment;
    relatedStories: IRelatedStoryMapper[];
  };
}

export interface IBundlePackage {
  id?: number;
  storyId: number;
  story?: IStory;
  targetType: 'ab_test' | 'default';
  showRemainAll: boolean;
  createdAt?: string;
  deletedAt?: string | null;
  prices: IBundlePrice[];
  minRemainEpisodesNumber?: number;
  maxRemainEpisodesNumber?: number;
}

export interface IBundlePrice {
  id?: number;
  showTitleAsAllRemaining?: boolean;
  bundleId?: number;
  episodeNum: number;
  price: number;
  createdAt?: string;
  pricePerEpisode?: number;
  originalEpisodePrice?: number;
  originalPrice?: number;
  originalEpisodeNum?: number;
  bonusEpisodeNum?: number;
  title?: string;
}

interface IFictionSerialsBase {
  type: 'collection' | 'themes' | 'storyBasedRecommended' | 'genres' | 'plusCatalog';
  data: ICollection | ITheme[] | IGenre[];
  curation: ICurationInfo;
}

interface IFictionSerialsCollection extends IFictionSerialsBase {
  type: 'collection' | 'storyBasedRecommended';
  data: ICollection;
}

interface IFictionSerialsThemes extends IFictionSerialsBase {
  type: 'themes';
  data: ITheme[];
}

interface IFictionSerialsGenres extends IFictionSerialsBase {
  type: 'genres';
  title: string;
  data: IGenre[];
}

interface IFictionSerialsPlusCatalog extends IFictionSerialsBase {
  type: 'plusCatalog';
}

type IFictionSerials = IFictionSerialsCollection | IFictionSerialsThemes | IFictionSerialsGenres | IFictionSerialsPlusCatalog;
interface IPromotion {
  type: 'story' | 'link' | 'collection';
  subTitle?: string;
  actionText?: string;
  object: IPromotionObject;
  curation: ICurationInfo;
}

interface ICurationInfo {
  id: number;
  priority: number;
  segmentId: number;
}

interface IPromotionObject {
  id: number;
  title: string;
  desc?: string;
  label?: string;
  coverUrl: string | null;
  additionalCoverHUrl: string | null;
  story?: IStory;
  link?: ILink;
  collection?: ICollection;
}

interface IPreviewSelection {
  isChecked: boolean;
  selectedSplitNumber: number;
}

export enum HomeNavigationType {
  Theme = 'theme',
  Genre = 'genre',
  NewlyAdded = 'newlyAdded',
  FreeReads = 'freeReads',
}

interface IHomeNavigationBaseType {
  type: HomeNavigationType;
  value?: ITheme | IGenre;
}

interface IHomeNavigationThemeType extends IHomeNavigationBaseType {
  type: HomeNavigationType.Theme;
  value: ITheme;
}

interface IHomeNavigationGenreType extends IHomeNavigationBaseType {
  type: HomeNavigationType.Genre;
  value: IGenre;
}

interface IHomeNavigationNewlyAddedType extends IHomeNavigationBaseType {
  type: HomeNavigationType.NewlyAdded;
}

interface IHomeNavigationFreeReadsType extends IHomeNavigationBaseType {
  type: HomeNavigationType.FreeReads;
}

export type IHomeNavigation = IHomeNavigationThemeType | IHomeNavigationGenreType | IHomeNavigationNewlyAddedType | IHomeNavigationFreeReadsType;

export interface IHomePreview {
  featuredTitle: string;
  promotions: IPromotion[];
  fictionSerials: IFictionSerials[];
  navigationSlots: IHomeNavigation[];
}

export interface IWriterLink {
  id: number;
  seasonId: number;
  season: ISeason;
  writerId: number;
  writer: IWriter;
  applyMonth: number;
  applyDay: number;
  createdAt: Date;
  deletedAt: Date | null;
}

export interface IStoryPriceSettingSchedule {
  id: number;
  storyId: number;
  story: IStory;
  price: number;
  creatorId: number;
  startsAt: string;
  appliedAt: string | null;
  createdAt: string;
  deletedAt: string | null;
}

export interface ITransfer {
  id: number;
  month: number;
  writerId: number;
  writer: IUser;
  transferredAt: DateString;
  transferredCoins: number;
  transferredAmount: number;
  redeemCoins: number;
  redeemedAmount: number;
  transfersByStory?: ITransferByStory[];
  mgReports?: IMinimumGuaranteeReport[];
}

export interface ITransferInformation {
  transferStories: ITransferByStory[];
  mgReports: IMinimumGuaranteeReport[];
}

export interface IMinimumGuaranteeReport {
  contract: ITransferContract;
  monthlyReports: IMinimumGuaranteeMonthlyReport[];
  paidCoins: number;
  adsCoins: number;
  freeCoins: number;
  paidEarnings: number;
  adsEarnings: number;
  cumulativeEarnings: number;
  remainAmount: number;
  receivableEarnings: number;
}

export interface IMinimumGuaranteeMonthlyReport {
  contract: ITransferContract;
  history: TransferContractMinimumGuaranteeHistory[];
  paidCoins: number;
  adsCoins: number;
  freeCoins: number;
  paidEarnings: number;
  adsEarnings: number;
  cumulativeEarnings: number;
  remainAmount: number;
  receivableEarnings: number;
  coinUnitPriceValue: number;
  isFinal: boolean;
}

export interface TransferContractMinimumGuaranteeHistory {
  minimumGuaranteeId: number;
  month: number;
  earnings: number;
  cumulativeEarnings: number;
  remainAmount: number;
  receivableEarnings: number;
}

export interface ITransferByStory {
  story: IStory;
  coins: number;
  amount: number;
  carriedOnCoins: number;
  carriedOnAmount: number;
  paidEarnings: number;
  adsEarnings: number;
  freeEarnings: number;
  radishPlusEarnings: number;
  transfersBySeason: ITransferBySeason[];
}

export interface ITransferBySeason {
  season: ISeason;
  coins: number;
  amount: number;
  carriedOnCoins: number;
  carriedOnAmount: number;
  paidEarnings: number;
  adsEarnings: number;
  freeEarnings: number;
  radishPlusEarnings: number;
}

interface ITransferContractBase {
  id: number;
  storyId: number | null;
  story?: IStory;
  seasonId: number | null;
  season?: ISeason;
  writerId: number | null;
  writer?: Partial<IWriter>;
  startsAt: DateString;
  endsAt: DateString;
  dealType: TransferContractDealType;
  value: number | string;
  originalStoryTitle: string;
  createdAt: DateString;
  minimumGuarantee?: Partial<IMinimumGuarantee>;
}

export interface ITransferContractByStory extends ITransferContractBase {
  storyId: number;
  story: IStory;
  seasonId: null;
  season: undefined;
}

export interface ITransferContractBySeason extends ITransferContractBase {
  storyId: null;
  story: undefined;
  seasonId: number;
  season: ISeason;
}

export interface ITransferContractByWriter extends ITransferContractBase {}

export type ITransferContract = ITransferContractByStory | ITransferContractBySeason | ITransferContractByWriter;

export enum TransferContractDealType {
  FlatFee = 'flat',
  RevenueShare = 'share',
  FixedCoinPrice = 'fixed',
  MinimumGuarantee = 'mg',
}

export interface IMinimumGuarantee {
  advancePayment: number;
  contractId: number;
  id: number;
  shareAfter: number;
  shareDuring: number;
}

export interface ICoinsInfo {
  credited: number;
  'one-time': number; // prop name should be changed...
  subscription: number;
  rewarded_video: number;
  offerwall: number;
  giveaway: number;
  earned: number;
  welcome: number;
  'radish-plus': number;
}

export interface ICoinUnitPrice {
  id: number;
  month: number; // YYYYMM
  google: number;
  apple: number;
  video: number;
  unitPrice: number;
  coins: ICoinsInfo;
  isConfirmed: boolean;
  radishPlusUsers: number;
  createdAt: DateString;
  radishPlusPool: number;
}

export interface IOnboardingFlow {
  id: number;
  title: string;
  flowKey: string;
  isActive: boolean;
  createdAt: DateString;
  updatedAt: DateString;
  defaultMatureTrackingId: string | null;
  defaultNonMatureTrackingId: string | null;
  defaultMatureCollection: ICollection | null;
  defaultNonMatureCollection: ICollection | null;
  flowLaneMappers: Partial<IOnboardingFlowLaneMapper>[];
}

export interface IOnboardingLane {
  id: number;
  title: string;
  themeName: string;
  thumbUrl: string;
  segmentId: number | null;
  liveCount: number;
  flowLaneMappers: IOnboardingFlowLaneMapper[];
  matureCollectionMappers: Partial<IOnboardingCollectionMapper>[];
  nonMatureCollectionMappers: Partial<IOnboardingCollectionMapper>[];
  createdAt: DateString;
  updatedAt: DateString;
}

export interface IOnboardingCollectionMapper {
  id: number;
  laneId: number;
  trackingId: string;
  segmentId: number | null;
  isMature: boolean;
  priority: number;
  collection: ICollection;
  themeName: string;
  contentTypes: StoryType[];
}

export interface IOnboardingFlowLaneMapper {
  id: number;
  flowId: number;
  laneId: number;
  priority: number;
  flow?: IOnboardingFlow;
  lane?: IOnboardingLane;
}

export interface ITransferMonthlyRoyaltyExpense {
  year: number;
  month: number;
  total: number;
  paidExcludingAds: number;
  ads: number;
  free: number;
}
export enum HomeType {
  READ = 'read' as any,
  LISTEN = 'listen' as any,
  ALL = 'all' as any,
}

export enum StoryType {
  Text = 'text',
  Chat = 'chat',
  WebComic = 'webcomic',
  Audio = 'audio',
}
